import React, { useState, useEffect } from 'react';
import {
    FaEye, FaSearch, FaSpinner, FaTimes,
    FaChevronDown, FaChevronUp, FaUsers,
    FaClipboardList, FaCalendarAlt, FaMapMarkerAlt,
    FaMoneyBillWave, FaCheckCircle, FaClock,
    FaBuilding, FaUser, FaEnvelope, FaPhone,
    FaCheck, FaTimes as FaTimesIcon, FaUserCheck,
    FaFilter, FaEdit
} from 'react-icons/fa';
import { getAllEnquiries, updateEnquiry } from '../../../Servicefile/salesService';

// Date filter options
const dateFilterOptions = ["All", "Today", "Yesterday", "This Week", "This Month", "Last Month", "This Year", "Custom Range"];

const ManagerAssigned = () => {
    // ============================================
    // STATE
    // ============================================
    const [leads, setLeads] = useState([]);
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Date filter states
    const [selectedDateFilter, setSelectedDateFilter] = useState("All");
    const [showDateDropdown, setShowDateDropdown] = useState(false);
    const [customFromDate, setCustomFromDate] = useState(() => {
        const date = new Date();
        date.setDate(1);
        return date.toISOString().split('T')[0];
    });
    const [customToDate, setCustomToDate] = useState(() => {
        return new Date().toISOString().split('T')[0];
    });
    const [showCustomPicker, setShowCustomPicker] = useState(false);
    const dateDropdownRef = React.useRef(null);

    // Modal states
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);

    // Close form
    const [closeReason, setCloseReason] = useState('');
    const [closeRemarks, setCloseRemarks] = useState('');

    // Stats
    const [stats, setStats] = useState({
        total: 0,
        assigned: 0,
        closed: 0
    });

    // ============================================
    // API CALLS
    // ============================================
    const fetchAssignedLeads = async () => {
        setLoading(true);
        try {
            const data = await getAllEnquiries();
            
            // Filter: Only show leads with status 'ASSIGNED'
            const assignedData = (data || []).filter(enquiry =>
                enquiry.leadStatus === 'ASSIGNED'
            );

            // Sort: Newest first
            const sortedData = assignedData.sort((a, b) => {
                const dateA = new Date(a.createdAt);
                const dateB = new Date(b.createdAt);
                return dateB - dateA;
            });

            setLeads(sortedData);
            setFilteredLeads(sortedData);
            calculateStats(sortedData);
        } catch (error) {
            console.error('Error fetching assigned leads:', error);
            alert('Failed to fetch assigned leads');
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (data) => {
        const total = data.length;
        const assigned = data.filter(e => e.leadStatus === 'ASSIGNED').length;
        const closed = data.filter(e => e.leadStatus === 'CLOSED').length;
        setStats({ total, assigned, closed });
    };

    // ============================================
    // DATE FILTER FUNCTIONS
    // ============================================
    const getDateRangeFromSelection = () => {
        if (selectedDateFilter === "All") return null;

        const today = new Date();
        let from = new Date();
        let to = new Date();

        switch (selectedDateFilter) {
            case "Today":
                from = today;
                to = today;
                break;
            case "Yesterday":
                from.setDate(today.getDate() - 1);
                to.setDate(today.getDate() - 1);
                break;
            case "This Week": {
                const day = today.getDay();
                from.setDate(today.getDate() - day + (day === 0 ? -6 : 1));
                to = new Date(from);
                to.setDate(from.getDate() + 6);
                break;
            }
            case "This Month":
                from = new Date(today.getFullYear(), today.getMonth(), 1);
                to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;
            case "Last Month":
                from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                to = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case "This Year":
                from = new Date(today.getFullYear(), 0, 1);
                to = new Date(today.getFullYear(), 11, 31);
                break;
            case "Custom Range":
                return { fromDate: customFromDate, toDate: customToDate };
            default:
                return null;
        }

        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        return {
            fromDate: formatDate(from),
            toDate: formatDate(to),
        };
    };

    const filterByDateRange = (items) => {
        const range = getDateRangeFromSelection();
        if (!range) return items;

        const startDate = new Date(range.fromDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(range.toDate);
        endDate.setHours(23, 59, 59, 999);

        return items.filter(item => {
            const createdDate = item.createdAt ? new Date(item.createdAt) : null;
            if (!createdDate) return false;
            return createdDate >= startDate && createdDate <= endDate;
        });
    };

    const handleDateFilterSelect = (filter) => {
        setSelectedDateFilter(filter);
        if (filter === "Custom Range") {
            setShowCustomPicker(true);
        } else {
            setShowCustomPicker(false);
            setShowDateDropdown(false);
        }
    };

    const applyCustomRange = () => {
        setShowCustomPicker(false);
        setShowDateDropdown(false);
    };

    const getDateFilterDisplayText = () => {
        if (selectedDateFilter === "All") return "All";
        if (selectedDateFilter === "Custom Range") {
            return `${customFromDate} to ${customToDate}`;
        }
        return selectedDateFilter;
    };

    // Close dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
                setShowDateDropdown(false);
                setShowCustomPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ============================================
    // HANDLERS
    // ============================================
    const handleCloseLead = async () => {
        if (!closeReason.trim()) {
            alert('Please provide a reason for closing');
            return;
        }

        setSubmitting(true);
        try {
            const leadId = selectedLead.id || selectedLead.enquiryId;
            
            const requestBody = {
                leadStatus: 'CLOSED',
                additionalRequests: closeRemarks || closeReason
            };

            await updateEnquiry(leadId, requestBody);

            // Update local state
            const updatedLeads = leads.map(lead =>
                (lead.id === leadId || lead.enquiryId === leadId)
                    ? { ...lead, leadStatus: 'CLOSED' }
                    : lead
            );
            
            // Remove closed leads from list (since they're no longer ASSIGNED)
            const remainingLeads = updatedLeads.filter(lead => lead.leadStatus !== 'CLOSED');
            
            setLeads(remainingLeads);
            setFilteredLeads(remainingLeads);
            calculateStats(remainingLeads);

            alert(`✅ Lead "${selectedLead.fullName}" has been closed successfully!`);
            setIsCloseModalOpen(false);
            setSelectedLead(null);
            setCloseReason('');
            setCloseRemarks('');
        } catch (error) {
            console.error('Error closing lead:', error);
            alert(error.response?.data?.message || 'Failed to close lead');
        } finally {
            setSubmitting(false);
        }
    };

    const handleViewLead = (lead) => {
        setSelectedLead(lead);
        setIsViewModalOpen(true);
    };

    const openCloseModal = (lead) => {
        setSelectedLead(lead);
        setCloseReason('');
        setCloseRemarks('');
        setIsCloseModalOpen(true);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    // ============================================
    // APPLY FILTERS
    // ============================================
    useEffect(() => {
        let result = leads;

        // Apply date filter
        result = filterByDateRange(result);

        // Apply search filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            result = result.filter(item =>
                (item.fullName || '').toLowerCase().includes(term) ||
                (item.email || '').toLowerCase().includes(term) ||
                (item.companyName || '').toLowerCase().includes(term) ||
                (item.city || '').toLowerCase().includes(term) ||
                (item.enquiryId || item.id || '').toString().includes(term)
            );
        }

        setFilteredLeads(result);
    }, [leads, searchTerm, selectedDateFilter, customFromDate, customToDate]);

    // ============================================
    // FORMAT HELPERS
    // ============================================
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹0';
        return `₹${Number(amount).toLocaleString('en-IN')}`;
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'ASSIGNED': { bg: '#E0E7FF', color: '#3730A3', label: 'Assigned', icon: <FaUserCheck size={12} /> },
            'CLOSED': { bg: '#F3F4F6', color: '#4B5563', label: 'Closed', icon: <FaCheck size={12} /> }
        };
        const info = statusMap[status] || statusMap['ASSIGNED'];
        return (
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: 600,
                background: info.bg,
                color: info.color
            }}>
                {info.icon} {info.label}
            </span>
        );
    };

    // ============================================
    // EFFECTS
    // ============================================
    useEffect(() => {
        fetchAssignedLeads();
    }, []);

    // ============================================
    // RENDER
    // ============================================
    return (
        <div style={containerStyle}>
       

            {/* Stats Cards */}
            <div style={statsContainerStyle}>
                <div style={statCardStyle}>
                    <div style={{ ...statIconWrapperStyle, background: '#EEF2FF' }}>
                        <FaUsers style={{ color: '#4F46E5' }} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{stats.total}</div>
                        <div style={statLabelStyle}>Total Assigned</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{ ...statIconWrapperStyle, background: '#E0E7FF' }}>
                        <FaUserCheck style={{ color: '#3730A3' }} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{stats.assigned}</div>
                        <div style={statLabelStyle}>Active</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{ ...statIconWrapperStyle, background: '#F3F4F6' }}>
                        <FaCheck style={{ color: '#4B5563' }} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{stats.closed}</div>
                        <div style={statLabelStyle}>Closed</div>
                    </div>
                </div>
            </div>

            {/* Search & Date Filter */}
            <div style={searchFilterContainerStyle}>
                <div style={searchContainerStyle}>
                    <FaSearch style={searchIconStyle} />
                    <input
                        type="text"
                        placeholder="Search by name, email, company, city..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={searchInputStyle}
                    />
                    {searchTerm && (
                        <button onClick={clearSearch} style={clearSearchButtonStyle}>
                            <FaTimes size={14} />
                        </button>
                    )}
                </div>

                {/* Date Filter */}
                <div ref={dateDropdownRef} style={{ position: "relative" }}>
                    <button
                        onClick={() => setShowDateDropdown(!showDateDropdown)}
                        style={{
                            ...dateFilterBtnStyle,
                            ...(showDateDropdown ? dateFilterBtnActiveStyle : {})
                        }}
                    >
                        <FaCalendarAlt size={14} color={showDateDropdown ? '#e66d33' : '#64748b'} />
                        <span>{getDateFilterDisplayText()}</span>
                        <FaChevronDown size={12} color={showDateDropdown ? '#e66d33' : '#64748b'} />
                    </button>

                    {showDateDropdown && !showCustomPicker && (
                        <div style={dropdownMenuStyle}>
                            {dateFilterOptions.map(option => (
                                <div
                                    key={option}
                                    onClick={() => handleDateFilterSelect(option)}
                                    style={{
                                        ...dropdownItemStyle,
                                        ...(selectedDateFilter === option ? dropdownItemActiveStyle : {})
                                    }}
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                    )}

                    {showCustomPicker && (
                        <div style={customPickerStyle}>
                            <div style={{ marginBottom: '12px' }}>
                                <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px', color: '#64748b' }}>From Date</label>
                                <input
                                    type="date"
                                    value={customFromDate}
                                    onChange={(e) => setCustomFromDate(e.target.value)}
                                    style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                                    max={customToDate}
                                />
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px', color: '#64748b' }}>To Date</label>
                                <input
                                    type="date"
                                    value={customToDate}
                                    onChange={(e) => setCustomToDate(e.target.value)}
                                    style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                                    min={customFromDate}
                                    max={new Date().toISOString().split('T')[0]}
                                />
                            </div>
                            <button
                                onClick={applyCustomRange}
                                style={{ width: '100%', padding: '10px', background: '#e66d33', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}
                            >
                                Apply Range
                            </button>
                        </div>
                    )}
                </div>

                <div style={resultCountStyle}>
                    {filteredLeads.length} result{filteredLeads.length !== 1 && 's'}
                </div>
            </div>

            {/* Table */}
            <div style={tableCardStyle}>
                {loading ? (
                    <div style={loadingStyle}>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={32} color="#4F46E5" />
                        <p>Loading assigned leads...</p>
                    </div>
                ) : (
                    <div style={tableWrapperStyle}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle, width: '4%', textAlign: 'center' }}>#</th>
                                    <th style={{ ...thStyle, width: '12%', textAlign: 'left' }}>Customer</th>
                                    <th style={{ ...thStyle, width: '10%', textAlign: 'center' }}>Event</th>
                                    <th style={{ ...thStyle, width: '10%', textAlign: 'center' }}>Date</th>
                                    <th style={{ ...thStyle, width: '8%', textAlign: 'center' }}>City</th>
                                    <th style={{ ...thStyle, width: '10%', textAlign: 'center' }}>Budget</th>
                                    <th style={{ ...thStyle, width: '10%', textAlign: 'center' }}>Status</th>
                                    <th style={{ ...thStyle, width: '36%', textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLeads.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" style={emptyStateStyle}>
                                            <div style={emptyStateContentStyle}>
                                                <FaUsers size={40} color="#D1D5DB" />
                                                <p>No assigned leads found</p>
                                                <span style={{ fontSize: '13px', color: '#9CA3AF' }}>
                                                    {searchTerm ? 'Try adjusting your search terms' : 'No leads currently assigned'}
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredLeads.map((lead, index) => (
                                        <tr key={lead.id || lead.enquiryId || index} style={tableRowStyle}>
                                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 500, color: '#9CA3AF' }}>
                                                {index + 1}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'left' }}>
                                                <div style={customerCellStyle}>
                                                    <div style={avatarStyle}>
                                                        {(lead.fullName || '?')[0].toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                                                            {lead.fullName || '-'}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                                            {lead.email || '-'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <span style={eventBadgeStyle}>
                                                    {lead.eventType || '-'}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center', fontSize: '12px', color: '#374151' }}>
                                                {lead.event === 'EVENT'
                                                    ? formatDate(lead.eventDate)
                                                    : `${formatDate(lead.fromDate)} - ${formatDate(lead.toDate)}`}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <span style={cityBadgeStyle}>
                                                    {lead.city || '-'}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, color: '#059669' }}>
                                                {formatCurrency(lead.budget)}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {getStatusBadge(lead.leadStatus)}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <div style={actionGroupStyle}>
                                                    <button
                                                        style={viewButtonStyle}
                                                        onClick={() => handleViewLead(lead)}
                                                        title="View Details"
                                                    >
                                                        <FaEye size={13} /> View
                                                    </button>
                                                    <button
                                                        style={closeButtonStyle}
                                                        onClick={() => openCloseModal(lead)}
                                                        title="Close Lead"
                                                    >
                                                        <FaCheck size={13} /> Close
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ========== VIEW LEAD MODAL ========== */}
            {isViewModalOpen && selectedLead && (
                <div style={modalOverlayStyle} onClick={() => setIsViewModalOpen(false)}>
                    <div style={{ ...modalStyle, maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
                        <div style={modalHeaderStyle}>
                            <div style={modalHeaderLeftStyle}>
                                <div style={modalHeaderIconStyle}>
                                    <FaEye size={18} />
                                </div>
                                <h3 style={modalTitleStyle}>Lead Details</h3>
                            </div>
                            <button onClick={() => setIsViewModalOpen(false)} style={modalCloseStyle}>
                                <FaTimes />
                            </button>
                        </div>

                        <div style={modalBodyStyle}>
                            <div style={viewSectionStyle}>
                                <h4 style={viewSectionTitleStyle}>Contact Information</h4>
                                <div style={viewGridStyle}>
                                    <div><strong>Name:</strong> {selectedLead.fullName || '-'}</div>
                                    <div><strong>Company:</strong> {selectedLead.companyName || '-'}</div>
                                    <div><strong>Email:</strong> {selectedLead.email || '-'}</div>
                                    <div><strong>Phone:</strong> {selectedLead.phoneNumber || '-'}</div>
                                    <div><strong>City:</strong> {selectedLead.city || '-'}</div>
                                    <div><strong>State:</strong> {selectedLead.state || '-'}</div>
                                </div>
                            </div>

                            <div style={viewSectionStyle}>
                                <h4 style={viewSectionTitleStyle}>Lead Information</h4>
                                <div style={viewGridStyle}>
                                    <div><strong>Source:</strong> {selectedLead.source || '-'}</div>
                                    <div><strong>Source By:</strong> {selectedLead.sourceBy || '-'}</div>
                                    <div><strong>Event:</strong> {selectedLead.eventType || '-'}</div>
                                    <div><strong>Event Date:</strong> {formatDate(selectedLead.eventDate)}</div>
                                    <div><strong>Budget:</strong> {formatCurrency(selectedLead.budget)}</div>
                                    <div><strong>Status:</strong> {getStatusBadge(selectedLead.leadStatus)}</div>
                                </div>
                            </div>

                            <div style={viewSectionStyle}>
                                <h4 style={viewSectionTitleStyle}>Additional Details</h4>
                                <div style={viewGridStyle}>
                                    <div><strong>Veg Plates:</strong> {selectedLead.vegPlates || 0}</div>
                                    <div><strong>Non-Veg Plates:</strong> {selectedLead.nonVegPlates || 0}</div>
                                    <div><strong>Mixed Plates:</strong> {selectedLead.mixedPlates || 0}</div>
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <strong>Address:</strong> {selectedLead.fullAddress || '-'}</div>
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <strong>Additional Requests:</strong> {selectedLead.additionalRequests || '-'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={formActionStyle}>
                            <button
                                onClick={() => setIsViewModalOpen(false)}
                                style={cancelButtonStyle}
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    setIsViewModalOpen(false);
                                    openCloseModal(selectedLead);
                                }}
                                style={closeButtonModalStyle}
                            >
                                <FaCheck size={14} /> Close Lead
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== CLOSE LEAD MODAL ========== */}
            {isCloseModalOpen && selectedLead && (
                <div style={modalOverlayStyle} onClick={() => setIsCloseModalOpen(false)}>
                    <div style={{ ...modalStyle, maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
                        <div style={modalHeaderStyle}>
                            <div style={modalHeaderLeftStyle}>
                                <div style={{ ...modalHeaderIconStyle, background: 'linear-gradient(135deg, #6B7280, #4B5563)' }}>
                                    <FaCheck size={18} />
                                </div>
                                <h3 style={modalTitleStyle}>Close Lead</h3>
                            </div>
                            <button onClick={() => setIsCloseModalOpen(false)} style={modalCloseStyle}>
                                <FaTimes />
                            </button>
                        </div>

                        <div style={modalBodyStyle}>
                            <div style={closeInfoStyle}>
                                <div><strong>Lead:</strong> {selectedLead.fullName}</div>
                                <div><strong>Company:</strong> {selectedLead.companyName || '-'}</div>
                                <div><strong>Budget:</strong> {formatCurrency(selectedLead.budget)}</div>
                            </div>

                            <div style={formGroupStyle}>
                                <label style={labelStyle}>Reason for Closing <span style={{ color: '#EF4444' }}>*</span></label>
                                <input
                                    type="text"
                                    value={closeReason}
                                    onChange={(e) => setCloseReason(e.target.value)}
                                    placeholder="e.g., Lead converted to client, Not interested, etc."
                                    style={inputStyle}
                                />
                            </div>

                            <div style={formGroupStyle}>
                                <label style={labelStyle}>Remarks (Optional)</label>
                                <textarea
                                    value={closeRemarks}
                                    onChange={(e) => setCloseRemarks(e.target.value)}
                                    rows="3"
                                    placeholder="Additional notes..."
                                    style={textareaStyle}
                                />
                            </div>

                            <div style={warningNoteStyle}>
                                ⚡ Closing this lead will move it to "Closed" status and it will no longer appear in the assigned list.
                            </div>
                        </div>

                        <div style={formActionStyle}>
                            <button
                                onClick={() => setIsCloseModalOpen(false)}
                                style={cancelButtonStyle}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCloseLead}
                                style={closeSubmitButtonStyle}
                                disabled={submitting || !closeReason.trim()}
                            >
                                {submitting ? (
                                    <>
                                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={14} />
                                        Closing...
                                    </>
                                ) : (
                                    <>
                                        <FaCheck size={14} />
                                        Confirm Close
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

// ─── Styles ──────────────────────────────────────────────────────

const containerStyle = {
    padding: '4px 0',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '16px',
};

const headerLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
};

const headerIconStyle = {
    width: '44px',
    height: '44px',
    background: 'linear-gradient(135deg, #e66d33, #e66d33)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
};

const titleStyle = {
    fontSize: '24px',
    fontWeight: 700,
    color: '#111827',
    margin: 0,
    letterSpacing: '-0.5px',
};

const subtitleStyle = {
    fontSize: '14px',
    color: '#6B7280',
    margin: '4px 0 0 0',
};

// Stats Styles
const statsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '20px',
};

const statCardStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    background: '#fff',
    padding: '16px 18px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #E5E7EB',
    transition: 'all 0.2s ease',
};

const statIconWrapperStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const statNumberStyle = {
    fontSize: '22px',
    fontWeight: 700,
    color: '#111827',
    lineHeight: 1.2,
};

const statLabelStyle = {
    fontSize: '13px',
    color: '#6B7280',
    fontWeight: 500,
};

// Search Styles
const searchFilterContainerStyle = {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
};

const searchContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: '200px',
    background: '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    padding: '0 16px',
    transition: 'all 0.2s ease',
};

const searchIconStyle = {
    color: '#9CA3AF',
    marginRight: '12px',
};

const searchInputStyle = {
    flex: 1,
    padding: '11px 0',
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    background: 'transparent',
    color: '#111827',
};

const clearSearchButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9CA3AF',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
};

const resultCountStyle = {
    fontSize: '13px',
    color: '#6B7280',
    fontWeight: 500,
};

// Date Filter Styles
const dateFilterBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    height: '38px',
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    color: '#334155',
};

const dateFilterBtnActiveStyle = {
    borderColor: '#e66d33',
    color: '#e66d33',
};

const dropdownMenuStyle = {
    position: 'absolute',
    top: '45px',
    left: 0,
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
    zIndex: 1000,
    minWidth: '180px',
    overflow: 'hidden',
};

const dropdownItemStyle = {
    padding: '10px 16px',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderBottom: '1px solid #f0f0f0',
};

const dropdownItemActiveStyle = {
    background: '#fef3e8',
    color: '#e66d33',
};

const customPickerStyle = {
    position: 'absolute',
    top: '45px',
    left: 0,
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
    zIndex: 1000,
    width: '280px',
    padding: '16px',
};

// Table Styles
const tableCardStyle = {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    border: '1px solid #E5E7EB',
};

const tableWrapperStyle = {
    overflowX: 'auto',
    padding: '0',
    maxHeight: 'calc(100vh - 420px)',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '900px',
};

const thStyle = {
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    background: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
    position: 'sticky',
    top: 0,
    zIndex: 10,
};

const tdStyle = {
    padding: '12px 16px',
    fontSize: '13px',
    color: '#111827',
    borderBottom: '1px solid #F3F4F6',
};

const tableRowStyle = {
    transition: 'background 0.15s ease',
    cursor: 'default',
};

const customerCellStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
};

const avatarStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #e66d33, #d45a2a)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 600,
    flexShrink: 0,
};

const eventBadgeStyle = {
    display: 'inline-block',
    padding: '2px 10px',
    background: '#F3F4F6',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#4B5563',
};

const cityBadgeStyle = {
    display: 'inline-block',
    padding: '2px 10px',
    background: '#EEF2FF',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#4F46E5',
};

// Action Buttons
const actionGroupStyle = {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    flexWrap: 'wrap',
};

const viewButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 14px',
    background: '#60A5FA',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    transition: 'all 0.2s ease',
};

const closeButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 14px',
    background: 'linear-gradient(135deg, #6B7280, #4B5563)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(75, 85, 99, 0.25)',
};

// Empty State
const emptyStateStyle = {
    padding: '60px 20px',
    textAlign: 'center',
    borderBottom: 'none',
};

const emptyStateContentStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
};

const loadingStyle = {
    padding: '60px 20px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    color: '#6B7280',
};

// Modal Styles
const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
    animation: 'fadeIn 0.25s ease',
};

const modalStyle = {
    background: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '720px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
    animation: 'slideUp 0.3s ease',
};

const modalHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #F3F4F6',
    position: 'sticky',
    top: 0,
    background: '#fff',
    zIndex: 1,
};

const modalHeaderLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
};

const modalHeaderIconStyle = {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #e66d33, #e66d33)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
};

const modalTitleStyle = {
    margin: 0,
    fontSize: '18px',
    fontWeight: 700,
    color: '#111827',
};

const modalCloseStyle = {
    background: '#F3F4F6',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    color: '#6B7280',
    padding: '6px 10px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
};

const modalBodyStyle = {
    padding: '24px',
};

// View Styles
const viewSectionStyle = {
    border: '1px solid #F3F4F6',
    borderRadius: '8px',
    padding: '14px 16px',
    marginBottom: '12px',
};

const viewSectionTitleStyle = {
    margin: '0 0 10px 0',
    fontSize: '14px',
    fontWeight: 600,
    color: '#1E293B',
};

const viewGridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
};

// Form Styles
const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    marginBottom: '16px',
};

const labelStyle = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
};

const inputStyle = {
    padding: '10px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s ease',
    background: '#fff',
    color: '#111827',
};

const textareaStyle = {
    ...inputStyle,
    resize: 'vertical',
    fontFamily: 'inherit',
    width: '100%',
};

const formActionStyle = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'space-between',
    padding: '16px',
};

const cancelButtonStyle = {
    padding: '10px 24px',
    background: '#F3F4F6',
    color: '#4B5563',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

const closeButtonModalStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 28px',
    background: 'linear-gradient(135deg, #6B7280, #4B5563)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(75, 85, 99, 0.3)',
};

const closeSubmitButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 28px',
    background: 'linear-gradient(135deg, #6B7280, #4B5563)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(75, 85, 99, 0.3)',
};

// Close Info
const closeInfoStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '12px',
    padding: '12px 16px',
    background: '#F9FAFB',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #E5E7EB',
};

const warningNoteStyle = {
    padding: '12px 16px',
    background: '#FEF3C7',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#92400E',
    border: '1px solid #FDE68A',
};

export default ManagerAssigned;